import type { TeamOfficeGetResponse } from "@bezoned/shared/types/api/team/team-office-get.types.js";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { recordErrorInSentry } from "../../../utils/init-sentry.js";
import { useOffice } from "../../office/use-office.js";
import { FLUID_ERROR_CODES } from "../error-codes.js";
import { joinFluidContainer } from "../fluid.client.js";
import type {
  FluidContainer,
  FluidContainerId,
  FluidServices,
  FluidSharedTree,
} from "../fluid.client.js";
import { RedirectToLobbyError } from "../fluid.errors.js";

const MAX_RETRY_ATTEMPTS = 3;

export function useFluidContainer() {
  const { team } = useOffice();
  const queryClient = useQueryClient();
  const [container, setContainer] = useState<FluidContainer | null>(null);
  const [containerId, setContainerId] = useState<FluidContainerId | null>(null);
  const [sharedTree, setSharedTree] = useState<FluidSharedTree | null>(null);
  const [services, setServices] = useState<FluidServices | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const retryCountRef = useRef(0);
  const activeContainerIdRef = useRef(team.fluidContainerId);
  const activeContainerRef = useRef<FluidContainer | null>(null);

  const cleanupActiveContainer = useCallback(async () => {
    const currentContainer = activeContainerRef.current;

    if (!currentContainer) {
      return;
    }

    activeContainerRef.current = null;

    try {
      await currentContainer.disconnect();
    } catch {
      // Ignore cleanup failures when tearing down a superseded container.
    }

    currentContainer.dispose();
  }, []);

  const initialize = useCallback(async () => {
    try {
      setError(null);
      const connection = await joinFluidContainer(activeContainerIdRef.current);

      // Reset retry count on successful connection
      retryCountRef.current = 0;
      activeContainerIdRef.current = connection.containerId;
      if (activeContainerRef.current !== connection.container) {
        cleanupActiveContainer().catch(() => {
          // Ignore cleanup failures when replacing an older container instance.
        });
      }
      activeContainerRef.current = connection.container;
      setContainer(connection.container);
      setContainerId(connection.containerId);
      setSharedTree(connection.sharedTree);
      setServices(connection.services);

      queryClient.setQueryData<TeamOfficeGetResponse>(
        ["teamOfficeGet", team.msTeamId],
        (currentOffice) => {
          if (
            !currentOffice ||
            currentOffice.team.fluidContainerId === connection.containerId
          ) {
            return currentOffice;
          }

          return {
            ...currentOffice,
            tableCallTokens: [],
            team: {
              ...currentOffice.team,
              fluidContainerId: connection.containerId,
            },
          };
        }
      );
      await queryClient.invalidateQueries({
        queryKey: ["teamOfficeGet", team.msTeamId],
      });
    } catch (caughtError) {
      retryCountRef.current += 1;

      recordErrorInSentry(
        caughtError instanceof Error
          ? caughtError
          : new Error("Failed to connect to Fluid container"),
        {
          extra: {
            attempt: retryCountRef.current,
            fluidContainerId: activeContainerIdRef.current,
            teamId: team.msTeamId,
          },
          tags: { event: "ContainerJoinFailed", feature: "fluid" },
        }
      );

      // If we've exceeded max retries, throw RedirectToLobbyError
      if (retryCountRef.current > MAX_RETRY_ATTEMPTS) {
        const redirectError = new RedirectToLobbyError(
          "Failed to connect to the office after multiple attempts",
          FLUID_ERROR_CODES.MAX_RETRIES_EXCEEDED
        );
        setError(redirectError);
        return;
      }

      setError(
        caughtError instanceof Error
          ? caughtError
          : new Error("Failed to connect")
      );
    } finally {
      setIsRetrying(false);
    }
  }, [cleanupActiveContainer, queryClient, team.msTeamId]);

  const retry = useCallback(() => {
    setIsRetrying(true);
    initialize();
  }, [initialize]);

  // Reset retry count when component unmounts or when manually resetting
  useEffect(
    () => () => {
      retryCountRef.current = 0;
      cleanupActiveContainer().catch(() => {
        // Ignore cleanup failures during teardown.
      });
    },
    [cleanupActiveContainer]
  );

  useEffect(() => {
    activeContainerIdRef.current = team.fluidContainerId;
    retryCountRef.current = 0;
  }, [team.fluidContainerId]);

  useEffect(() => {
    let mounted = true;

    const initializeIfMounted = async () => {
      if (!mounted) {
        return;
      }
      await initialize();
    };

    initializeIfMounted();

    return () => {
      mounted = false;
      cleanupActiveContainer().catch(() => {
        // Ignore cleanup failures during teardown.
      });
      setContainer(null);
      setContainerId(null);
      setSharedTree(null);
      setServices(null);
      setError(null);
    };
  }, [cleanupActiveContainer, initialize]);

  return {
    container,
    containerId,
    error,
    isRetrying,
    retry,
    services,
    sharedTree,
  };
}
